<script setup>
import { ref, computed, watch } from 'vue';
import { formataCpf, removeAcentos } from '@kodatec/brasil-utils';
import { usePessoasApi } from '@/modules/pessoas/composables/usePessoasApi';
import { useRuntimeConfig } from '@/composables/useRuntimeConfig';

const {
    listaPessoasFisicas,
    buscarPessoasFisicasPorFiltro,
} = usePessoasApi();

const { buscarPfApenasPorCpf } = useRuntimeConfig();

const props = defineProps({
    modelValue: {
        type: Number,
        default: null,
    },
    id: {
        type: String,
        default: 'pessoa-fisica-campo',
    },
    cpf: {
        type: String,
        default: '',
    },
    erro: {
        type: String,
        default: null,
    },
    titulo: {
        type: String,
        default: 'Pessoa Física',
    },
    obrigatorio: {
        type: Boolean,
        default: true,
    },
    placeholder: {
        type: String,
        default: 'Informe a pessoa física',
    },
    disabled: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(['update:modelValue', 'change']);

const pessoaSelecionada = ref(null);

// Quando a flag está ativa, a busca é feita apenas pelo CPF informado
const placeholderCampo = computed(() =>
    buscarPfApenasPorCpf.value ? 'Informe o CPF' : props.placeholder
);

const helpCampo = computed(() =>
    buscarPfApenasPorCpf.value
        ? 'A busca é feita apenas pelo CPF da pessoa física.'
        : null
);

const apenasNumeros = (event) => {
    if (!buscarPfApenasPorCpf.value) return;
    const teclaNavegacao = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Home', 'End'];
    if (event.ctrlKey || event.metaKey || teclaNavegacao.includes(event.key)) return;
    if (!/^\d$/.test(event.key)) event.preventDefault();
};

const colarApenasNumeros = (event) => {
    if (!buscarPfApenasPorCpf.value) return;
    const texto = (event.clipboardData || window.clipboardData).getData('text');
    if (/\D/.test(texto)) event.preventDefault();
};

const buscarPessoaFisica = async (event) => {
    const termo = buscarPfApenasPorCpf.value
        ? event.query.replace(/\D/g, '')
        : removeAcentos(event.query);
    if (!termo) return;
    await buscarPessoasFisicasPorFiltro(termo);
};

const selecionarPessoa = (event) => {
    if (event && typeof event.value === 'object' && event.value !== null) {
        emit('update:modelValue', event.value.id);
        emit('change', event.value);
    } else {
        emit('update:modelValue', null);
        emit('change', null);
    }
};

// Sincroniza o valor inicial (edição): busca a pessoa pelo nome e seleciona pelo id
watch(
    () => props.modelValue,
    async (novoValor) => {
        if (!novoValor) {
            if (pessoaSelecionada.value !== null) {
                pessoaSelecionada.value = null;
            }
            return;
        }

        if (pessoaSelecionada.value?.id === novoValor) {
            return;
        }

        const termoBusca = buscarPfApenasPorCpf.value
            ? props.cpf.replace(/\D/g, '')
            : removeAcentos(props.cpf);
        if (!termoBusca) return;
        await buscarPessoasFisicasPorFiltro(termoBusca);
        const pessoa = listaPessoasFisicas.value.find((item) => item.id === novoValor);
        if (pessoa) {
            pessoaSelecionada.value = pessoa;
            emit('change', pessoa);
        }
    },
    { immediate: true }
);
</script>

<template>
    <div class="field mb-2 col-12" :id="props.id">
        <SmallTitle :titulo="props.titulo" :obrigatorio="props.obrigatorio" :help="helpCampo" />
        <AutoComplete v-model="pessoaSelecionada" forceSelection :delay="1000" :suggestions="listaPessoasFisicas"
            @change="selecionarPessoa" @complete="buscarPessoaFisica" @keypress="apenasNumeros"
            @paste="colarApenasNumeros" :placeholder="placeholderCampo" optionLabel="nome" inputClass="text-sm"
            :disabled="props.disabled" :class="{ 'p-invalid': props.erro }">
            <template #option="slotProps">
                <div class="flex flex-row align-items-center">
                    <div class="flex flex-column">
                        <p class="m-0 p-0 font-semibold text-sm">{{ slotProps.option.nome }}</p>
                        <span class="text-xs">{{ formataCpf(slotProps.option.cpf) }}</span>
                    </div>
                </div>
            </template>
        </AutoComplete>
        <SmallError :error="props.erro" />
    </div>
</template>
